import { lazy, type ComponentType } from 'react';

/**
 * A minimal counter + pub-sub, not a state library — this app has no global
 * store, and one wasn't worth adding just to know "is a route chunk still
 * being fetched." A counter (not a boolean) because two lazy imports could
 * plausibly overlap (a fast double-click between two nav links); the bar
 * should only disappear once every in-flight one has settled.
 */
let pendingCount = 0;
const listeners = new Set<(loading: boolean) => void>();

function notify() {
  const loading = pendingCount > 0;
  listeners.forEach((fn) => fn(loading));
}

export function subscribeRouteLoad(fn: (loading: boolean) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Drop-in replacement for `React.lazy` that also reports into the counter
 * above — every `const XPage = lazy(...)` in App.tsx becomes
 * `lazyRoute(...)` and the progress bar just works, no per-route wiring.
 */
export function lazyRoute<T extends ComponentType<unknown>>(loader: () => Promise<{ default: T }>) {
  return lazy(() => {
    pendingCount += 1;
    notify();
    return loader().finally(() => {
      pendingCount = Math.max(0, pendingCount - 1);
      notify();
    });
  });
}
